import { Button } from "@datamole/wds-component-button";
import { Card } from "@datamole/wds-component-card";
import { Combobox, useCombobox, useComboboxMultiple } from "@datamole/wds-component-combobox";
import { Dialog } from "@datamole/wds-component-dialog";
import { Input } from "@datamole/wds-component-input";
import { Tooltip } from "@datamole/wds-component-tooltip";
import { faEdit, faMapPin, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Amenity, RoomType } from "@office-map/api-schema";
import React, { useContext, useState } from "react";

import styles from "./entity-card.module.css";

import { useRole } from "@/features/auth/role.hooks";
import { Label } from "@/features/info-popover/entity-cards/label";
import { InfoPopoverContext, RoomTrigger } from "@/features/info-popover/popover.context";
import { NavigateAction } from "@/features/navigation/NavigateAction";
import { storageService } from "@/features/storage/storage.service";
import { amenityTypes, getAmenityLabel, getRoomTypeLabel, roomTypes } from "@/features/storage/storage.utils";
import { useStorageSubscription } from "@/features/storage/use-storage-subscription";

type Props = {
    trigger: RoomTrigger;
};

const displayAmenities = (amenity: Amenity) => {
    if (!amenity) {
        return "None";
    }

    return (
        <Tooltip key={amenity}>
            <Tooltip.Trigger asChild>
                {/* ?v=2 cache-busts previously cached URLs (images were previosly served with a 1-year immutable cache) */}
                <img alt={getAmenityLabel(amenity)} width={30} src={`/amenities/${amenity}.png?v=2`} />
            </Tooltip.Trigger>
            <Tooltip.Content>{getAmenityLabel(amenity)}</Tooltip.Content>
        </Tooltip>
    );
};

export const RoomHeader = ({ trigger }: Props) => {
    const room = storageService.getRooms().find((room) => room.id === trigger.roomId);
    if (!room) return null;

    return (
        <Card.Header.Description>
            <Card.Header.Description.Title style={{ fontSize: 24 }}>
                <FontAwesomeIcon icon={faMapPin} style={{ marginRight: 16, color: "var(--wds-color-primary-700)" }} />
                {room.name}
            </Card.Header.Description.Title>
        </Card.Header.Description>
    );
};

export const RoomCard = ({ trigger }: Props) => {
    useStorageSubscription();

    const room = storageService.getRooms().find((room) => room.id === trigger.roomId);
    if (!room) {
        return <div>No data found for room {trigger.roomId}</div>;
    }
    const totalSeats = room.seats.length;
    const occupiedSeats = room.seats.filter((seat) => seat.occupiedBy !== null).length;

    return (
        <div className={styles.card_info}>
            <div>
                <Label>Room type:</Label> {getRoomTypeLabel(room.type)}
            </div>
            {room.type === "office" && (
                <div>
                    {" "}
                    <div>
                        <Label>Total seats:</Label> {totalSeats}
                    </div>
                    <div>
                        <Label>Occupied seats:</Label> {occupiedSeats}
                    </div>
                    <div>
                        <Label>Unoccupied seats:</Label> {totalSeats - occupiedSeats}
                    </div>
                </div>
            )}
            {room.type === "meeting-room" && (
                <div>
                    <Label>Total seats:</Label> {room.numberOfSeats}
                </div>
            )}
            {room.amenities && room.amenities.length > 0 && (
                <div>
                    <Label>Amenities:</Label>{" "}
                    <div className={styles.room_amenities}>{room.amenities?.map(displayAmenities)}</div>
                </div>
            )}
        </div>
    );
};

export const RoomActions = ({ trigger }: Props) => {
    const { setTrigger } = useContext(InfoPopoverContext);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingRoomName, setEditingRoomName] = useState("");
    const [editingNumberOfSeats, setEditingNumberOfSeats] = useState(0);
    const { isAdmin } = useRole();
    const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
    const [selectedType, setSelectedType] = useState<RoomType | null>(null);
    const startRoomId = storageService.getMyLocation()?.roomId;
    const amenityCombobox = useComboboxMultiple({
        options: amenityTypes,
        value: selectedAmenities,
        onChange: setSelectedAmenities,
        getOptionLabel: getAmenityLabel,
        getOptionValue: (option) => option,
    });
    const roomTypeCombobox = useCombobox({
        options: roomTypes,
        value: selectedType,
        onChange: setSelectedType,
        getOptionLabel: getRoomTypeLabel,
        getOptionValue: (option) => option,
    });

    useStorageSubscription();

    const room = storageService.getRooms().find((room) => room.id === trigger.roomId);
    if (!room) {
        return <div>No data found for room {trigger.roomId}</div>;
    }

    const handleOpenEditDialog = () => {
        setSelectedType(room.type);
        setEditingRoomName(room.name);
        setSelectedAmenities(room.amenities ?? []);
        setEditingNumberOfSeats(room.numberOfSeats ?? 0);
        setIsEditDialogOpen(true);
    };

    const handleEditSubmit = () => {
        if (selectedType) {
            storageService.changeRoomType(room.id, selectedType);
        }
        storageService.changeRoomName(room.id, editingRoomName);
        storageService.editAmenities(room.id, selectedAmenities);
        if (selectedType === "meeting-room") {
            storageService.changeNumberOfSeats(room.id, editingNumberOfSeats);
        }
        setIsEditDialogOpen(false);
    };

    const actions = [];

    if (isAdmin) {
        actions.push(
            <Button
                key={"add-seat-action"}
                onClick={() => {
                    const seatId = storageService.addSeat(trigger.roomId)!;
                    setTrigger({ type: "empty-seat", seatId: seatId });
                }}
            >
                <Button.Icon>
                    <FontAwesomeIcon icon={faPlus} />
                </Button.Icon>
                <Button.Content>Add Seat</Button.Content>
            </Button>,
        );

        actions.push(
            <React.Fragment key="edit-room-details-action">
                <Button onClick={handleOpenEditDialog}>
                    <Button.Icon>
                        <FontAwesomeIcon icon={faEdit} />
                    </Button.Icon>
                    <Button.Content>Edit room details</Button.Content>
                </Button>
                <Dialog open={isEditDialogOpen}>
                    <Dialog.Content style={{ padding: "20px", gap: "16px", maxWidth: "400px" }}>
                        <h2 style={{ marginTop: 0 }}>Edit room details</h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <label>Room type</label>
                            <Combobox {...roomTypeCombobox.getRootProps()}>
                                <Combobox.Trigger>
                                    <Combobox.Trigger.Value>
                                        {selectedType ? getRoomTypeLabel(selectedType) : "Select type"}
                                    </Combobox.Trigger.Value>
                                </Combobox.Trigger>
                                <Combobox.Content {...roomTypeCombobox.getContentProps()}>
                                    <Combobox.Content.SearchBox
                                        placeholder="Type to search"
                                        {...roomTypeCombobox.getSearchBoxProps()}
                                    />
                                    <Combobox.Content.List {...roomTypeCombobox.getListProps()}>
                                        {roomTypeCombobox.options.length < 1 ? (
                                            <Combobox.Content.Info>No results found.</Combobox.Content.Info>
                                        ) : (
                                            roomTypeCombobox.options.map((option, index) => (
                                                <Combobox.Content.List.Option
                                                    key={option}
                                                    {...roomTypeCombobox.getOptionProps(index)}
                                                >
                                                    {getRoomTypeLabel(option)}
                                                </Combobox.Content.List.Option>
                                            ))
                                        )}
                                    </Combobox.Content.List>
                                </Combobox.Content>
                            </Combobox>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <label>Room name</label>
                            <Input
                                value={editingRoomName}
                                onChange={(event) => setEditingRoomName(event.target.value)}
                            />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <label>Amenities</label>
                            <Combobox {...amenityCombobox.getRootProps()}>
                                <Combobox.Trigger>
                                    <Combobox.Trigger.ValueMultiple>
                                        {amenityCombobox.valueOptions.map((option) => (
                                            <Combobox.Trigger.ValueMultiple.Item
                                                key={option}
                                                {...amenityCombobox.getValueMultipleItemProps(option)}
                                            >
                                                {getAmenityLabel(option)}
                                            </Combobox.Trigger.ValueMultiple.Item>
                                        ))}
                                    </Combobox.Trigger.ValueMultiple>
                                </Combobox.Trigger>
                                <Combobox.Content {...amenityCombobox.getContentProps()}>
                                    <Combobox.Content.SearchBox
                                        placeholder="Type to search"
                                        {...amenityCombobox.getSearchBoxProps()}
                                    />
                                    <Combobox.Content.List {...amenityCombobox.getListProps()}>
                                        {amenityCombobox.options.length < 1 ? (
                                            <Combobox.Content.Info>No results found.</Combobox.Content.Info>
                                        ) : (
                                            amenityCombobox.options.map((option, index) => (
                                                <Combobox.Content.List.Option
                                                    key={option}
                                                    {...amenityCombobox.getOptionProps(index)}
                                                >
                                                    {getAmenityLabel(option)}
                                                </Combobox.Content.List.Option>
                                            ))
                                        )}
                                    </Combobox.Content.List>
                                </Combobox.Content>
                            </Combobox>
                        </div>
                        {selectedType === "meeting-room" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                <label>Number of seats</label>
                                <Input
                                    type="number"
                                    value={editingNumberOfSeats}
                                    onChange={(event) => setEditingNumberOfSeats(Number(event.target.value))}
                                />
                            </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Button variant={"primary"} onClick={handleEditSubmit}>
                                Submit
                            </Button>
                            <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                        </div>
                    </Dialog.Content>
                </Dialog>
            </React.Fragment>,
        );
    }

    if (startRoomId) {
        actions.push(<NavigateAction key="navigate-action" startRoomId={startRoomId} endRoomId={trigger.roomId} />);
    }

    return <>{actions}</>;
};
